import { useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import ContentSection from '../../components/UI/ContentSection'
import TableToolbar from '../../components/Table/TableToolbar'
import OrganizationsList, {
  organizationCsvColumns,
} from '../../components/OrganizationsList/OrganizationsList'
import useOrganizations from '../../services/organizations/useOrganizations'
import { downloadCsv } from '../../utils/toCsv'
import { filterByColumns } from '../../utils/tableSearch'
import { matchesOrganizationStatus } from '../../utils/tableFilters'
import FilterSelect from '../../components/Table/FilterSelect'

const Organizations = () => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const { data: organizations, isLoading: isOrganizationsLoading } =
    useOrganizations()

  const isExactPath =
    pathname === '/super-admin' || pathname === '/super-admin/organizations'

  useEffect(() => {
    document.title = `${t('organizations')} - ReportAll`
  }, [t])

  const csvColumns = useMemo(() => organizationCsvColumns(t), [t])

  // The backend can filter is_active, is_school, is_deleted and timezone,
  // but not status - it is computed from the subscription. Status is the one
  // an operator actually reaches for, and on the live data the other four
  // hold a single value across every organisation, so all of this is done
  // here over the array the list already has.
  const statusOptions = useMemo(
    () => [
      { value: 'active', label: t('active') },
      { value: 'pending', label: t('pending') },
    ],
    [t],
  )

  const rows = useMemo(() => {
    const byStatus = (organizations ?? []).filter((organization) =>
      matchesOrganizationStatus(organization.status, status),
    )
    return filterByColumns(csvColumns, byStatus, search)
  }, [csvColumns, organizations, search, status])

  const handleExport = () => {
    downloadCsv(
      `organisations-${moment().format('YYYY-MM-DD')}.csv`,
      csvColumns,
      rows,
    )
  }

  return (
    <ContentSection>
      {isExactPath ? (
        <>
          <TableToolbar
            title={t('organizations')}
            count={rows.length}
            search={search}
            onSearchChange={setSearch}
            filters={
              <FilterSelect
                allLabel={t('allStatuses')}
                value={status}
                options={statusOptions}
                onChange={setStatus}
              />
            }
            onExport={handleExport}
            isExportDisabled={rows.length === 0}
          />
          <OrganizationsList
            isLoading={isOrganizationsLoading}
            organizations={rows}
          />
        </>
      ) : (
        <Outlet />
      )}
    </ContentSection>
  )
}

export default Organizations
